import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantMeQueryKey } from '../tenant-auth/queries.js'
import {
  acceptTenantInvite,
  createTenantInvite,
  fetchTenantTeam,
  removeTenantMember,
  revokeTenantInvite,
} from './api.js'

const QUERY_KEY = ['tenant-team'] as const

export function useTenantTeam() {
  return useQuery({ queryKey: QUERY_KEY, queryFn: fetchTenantTeam })
}

export function useCreateTenantInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createTenantInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

/**
 * Accepting mints a brand-new session (`PLT-086`'s `issueSession`, called
 * directly by this endpoint) — the browser now holds a real
 * `pruvato_tenant_session` cookie it didn't have a moment ago. Invalidating
 * `tenant-me` (rather than leaving its cached `null` from before this
 * mutation ran) is what makes `RequireTenantAuth` treat the very next
 * navigation as signed in instead of bouncing back to `/customer/login`.
 */
export function useAcceptTenantInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ inviteId, otp, name }: { inviteId: string; otp: string; name: string }) =>
      acceptTenantInvite(inviteId, { otp, name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: tenantMeQueryKey })
    },
  })
}

export function useRevokeTenantInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: revokeTenantInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useRemoveTenantMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeTenantMember,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}
